/**
 * 前払い残高（/money/prepaid）: 残高・何日もつか・入金・残高のお知らせの線・自動チャージ・売上からの充当・履歴。
 * サーバー: GET /v1/money/prepaid（daysLeft・notices つき）・/prepaid/ledger、PUT /prepaid/settings（money/routes.ts・lowBalance.ts）、
 *          POST /v1/money/prepaid/checkout・GET /prepaid/checkouts/:id（payments/prepaidTopup.ts）。
 *
 * 利用料は原価のまま前払いから引く（無料枠なし・MASTER-PLAN）。残高切れに猶予は無く、請求残が出ると Functions が止まり新規作成と
 * デプロイが 402 になる。だから一番上に「いくらあって・何日もつか・止まっていないか」を出し、入金と自動チャージをすぐ下に置く。
 *
 * 並び（2026-10-18 design/rauno-dashboard・docs/teams/design/rauno-audit.md「一番先に直す 5 つ」の 5）:
 *   見出し → 1 行（料金は原価どおり + 「料金のしくみ」へのリンク）→ 残高の札 → 知らせ → 入金 | お知らせと自動チャージ → 履歴 → 料金のしくみ。
 *   以前は説明の 3 段落（pp.lead・PriceNotes）の下に残高があり（PC で上から 360px・スマホで 520px）、手数料の説明が入金の欄に
 *   3 回（見積もりの 1 行・feeNote・例の表）出ていた。文は 1 つも変えず、長い説明（pp.lead・refundOnly・feeNote・例）を下の段へ移しただけ。
 *   入金の欄に残す手数料の文は見積もりの 1 行（pp.feeLine = いま払う額）だけ。主のボタンは「支払う」1 つ（幅いっぱい）・設定の「保存」は次のボタン
 *
 * 入金: 本物の Stripe では Checkout へ移り、戻り先の `?kumo_topup=<id>` で状態をポーリングして結果を出す（webhook の遅れの救済は
 * サーバーの GET …/checkouts/:id がやる）。鍵の無いローカルでは偽決済 = その場で入金済みの応答が返る。
 * 入金は手動キャプチャ（3ds-refuse）なので、状態が completed になるまで「入金しました」と言わない。断られたら（card_not_protected）
 * 「別のカードを」とだけ言う（2026-09-27 money/dashboard-3ds-self・判断は moneyDisplay.ts topUpOutcome）。
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../../api';
import { ago, ErrorState, fullDate, PageHeader, Spinner, StatTile } from '../../components/ui';
import { usePagedResource, useResource } from '../../hooks';
import { LoadMore } from '../../components/LoadMore';
import { errorText, translate, useT, type MessageKey } from '../../i18n';
import { money } from '../../money';
import type { PrepaidLedgerLine, PrepaidView, TopUpQuote, TopUpView } from '../../moneyApi';
import { amountToInput, balanceLineHint, parseAmountInput, prepaidTone, topUpInRange, type TopUpLimits } from '../../moneyLogic';
import { prepaidFundingLocked, prepaidPageAlert, prepaidTileTones, TOPUP_CANCELED_PARAM, topUpOutcome, type TopUpOutcome } from './moneyDisplay';
import { isLaunchHidden } from '../../launchHidden';
import { readInviteStanding } from '../../inviteCode';
import { inviteMostly, prepaidBand } from '../../prepaidGate';
import { PriceNotes } from './PriceNotes';
import { useSession } from '../../session';

/**
 * 入金額の候補（セント）。上限・下限は画面に写さず、サーバーの topUpLimits（money/prepaid.ts の TOPUP_LIMITS）を読む
 * （2026-10-04 console/limits-ui。以前は TOPUP_MIN = 500 / TOPUP_MAX = 1,000,000 を写していた = Money が下限を下げても画面が止め続ける）
 */
const PRESETS = [1000, 2500, 5000, 10000];

const SOURCE_KEYS: Record<string, MessageKey> = {
  prepaid_topup: 'pp.src.prepaid_topup',
  usage_charge: 'pp.src.usage_charge',
  usage_refund: 'pp.src.usage_refund',
  prepaid_fund: 'pp.src.prepaid_fund',
  prepaid_offset: 'pp.src.prepaid_offset',
  prepaid_expire: 'pp.src.prepaid_expire',
  prepaid_refund: 'pp.src.prepaid_refund',
  prepaid_dispute: 'pp.src.prepaid_dispute',
  prepaid_dispute_won: 'pp.src.prepaid_dispute_won',
  // 自分の支払い（売上に含めない）を保留の期日に前払いへ移した（money/selfPurchase.ts・0252）
  self_purchase_to_prepaid: 'pp.src.self_purchase_to_prepaid',
  // 招待クレジットが入った（money/prepaid.ts creditInvite・0268・2026-09-29 launch-close-2 で表示名。以前は 'invite_credit' がそのまま出ていた）
  invite_credit: 'pp.src.invite_credit'
};
const sourceLabel = (s: string) => (SOURCE_KEYS[s] ? translate(SOURCE_KEYS[s]) : s);

/**
 * 入金の結果の通知（戻り先のポーリングと、偽決済のその場の応答で同じ言い方にする）。
 * checking がここに来る = 20 秒待っても決まらない（webhook の遅れ等）。完了とは言わず、決まれば残高に出るとだけ言う
 */
function topUpNotice(outcome: TopUpOutcome, topup: TopUpView, t: ReturnType<typeof useT>['t']): { tone: 'ok' | 'info' | 'error'; text: string } {
  switch (outcome) {
    case 'done':
      return { tone: 'ok', text: t('pp.topUpDone', { amount: money(topup.credited ?? topup.amount, topup.currency) }) };
    case 'checking':
      return { tone: 'info', text: t('pp.topUpStillChecking') };
    case 'card_refused':
      // 理由（3D セキュア・不正対策）は書かない（moneyDisplay.ts topUpOutcome の WHY）
      return { tone: 'error', text: t('pp.topUpCardRefused') };
    default:
      return { tone: 'error', text: t('pp.topUpFailed') };
  }
}

export function PrepaidPage() {
  const r = useResource<PrepaidView>('/v1/money/prepaid');
  // 30 件ずつ「さらに読み込む」で古い明細へ（2026-10-04 console/limits-ui。以前は 30 件で切れて、それより古い明細が見えなかった）
  const ledger = usePagedResource<{ entries: PrepaidLedgerLine[]; nextCursor?: string | null }, 'entries'>('/v1/money/prepaid/ledger?limit=30', 'entries');
  const { t } = useT();
  const { developer } = useSession();
  const [notice, setNotice] = useState<{ tone: 'ok' | 'info' | 'error'; text: string } | null>(null);
  const [params, setParams] = useSearchParams();
  const reloadAll = () => {
    r.reload();
    ledger.reload();
  };

  // Checkout から戻ってきた: 入金の状態を最大 20 秒ポーリングして結果を出す。
  // 手動キャプチャ（3ds-refuse）なので、戻った時点では決まっていない。completed になるまで「確認しています」のまま
  // （戻った = 完了、と扱うと一瞬「入金しました」と出てから断られることがある = moneyDisplay.ts topUpOutcome）
  const returned = params.get('kumo_topup');
  const canceled = params.get(TOPUP_CANCELED_PARAM) !== null;
  useEffect(() => {
    if (!returned) return;
    let alive = true;
    let tries = 0;
    // 結果を出したら URL から外す（再読み込みで同じ通知を繰り返さない）
    const clearParams = () =>
      setParams(
        (p) => {
          p.delete('kumo_topup');
          p.delete(TOPUP_CANCELED_PARAM);
          return p;
        },
        { replace: true }
      );
    if (canceled) {
      // キャンセルの戻り先: 入金は期限切れまで pending のまま = ポーリングしても決まらない。お金は動いていないとだけ言う
      setNotice({ tone: 'info', text: t('pp.topUpCanceled') });
      clearParams();
      return;
    }
    setNotice({ tone: 'info', text: t('pp.topUpPending') });
    const tick = async () => {
      try {
        const { topup } = await api<{ topup: TopUpView }>(`/v1/money/prepaid/checkouts/${encodeURIComponent(returned)}`);
        if (!alive) return;
        const outcome = topUpOutcome(topup);
        if (outcome === 'checking' && ++tries < 10) {
          window.setTimeout(() => void tick(), 2000);
          return;
        }
        setNotice(topUpNotice(outcome, topup, t));
        if (outcome === 'done') reloadAll();
      } catch (e) {
        if (alive) setNotice({ tone: 'error', text: errorText(e as ApiError) });
      }
      if (alive) clearParams();
    };
    void tick();
    return () => {
      alive = false;
    };
    // returned が変わったときだけ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returned]);

  const p = r.data;
  // ほかの画面（利用料の「料金のしくみ」）から #prepaid-pricing で来たとき、読み込み後に段まで送る（2026-10-18 design/rauno-dashboard2）。
  // 段は残高を読んでから描くので、ブラウザの自動のスクロールが間に合わない。同じ画面の中のリンク（上の 1 行）はブラウザがそのまま送る
  const loaded = !!p;
  useEffect(() => {
    if (loaded && window.location.hash) document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
  }, [loaded]);
  // 「少ない」の線は、サーバーがいま効かせている線（lowLineCents）を優先する（2026-09-29 launch-close-2）。
  // WHY: 招待クレジットがほとんどの間、サーバーは線を招待額の 20% に下げる（lowBalance.ts effectiveLowLine）= 入った直後の $5 は「少ない」ではない。
  //   設定の線（lowBalanceCents）のままだと、知らせのメールは来ないのに画面だけ「残高が少ない」と言う。古い本体（lowLineCents 無し）は設定の線。
  //   2026-09-30 console/words-2（N1）: サーバーの balanceStatus（low / out = 知らせのメールと同じ判定）も効かせる（moneyLogic.ts prepaidTone）
  const tone = p ? prepaidTone(p) : null;
  // 残高の欄の下の「○○を下回ったらお知らせ」: 効いている線が設定と違う（招待クレジットの間）ときは Money の文で両方を出す（balanceLineHint）
  const lineHint = p ? balanceLineHint(p) : null;
  // 招待クレジット（Money の前払いの道の一覧 18）: 入っている（確認済み）ときだけ残高の下に内訳の 1 行。確認待ちは下の InviteCodeCard（MoneyShell）が言う
  const invite = p ? readInviteStanding(p.invite) : null;
  const inviteLeft = invite && !invite.pending && invite.granted > 0 ? invite : null;
  // 残高の知らせ（一覧 20・lowBalance.ts の notices = 解決していないもの）: low_balance / balance_due。detail.invite = 残りのほとんどが招待クレジット
  const lowNotice = (p?.notices ?? []).find((n) => n.kind === 'low_balance');
  const dueNotice = (p?.notices ?? []).find((n) => n.kind === 'balance_due');
  // 「少ない」の文を招待向けにするか: 知らせが招待と言っている、または今の残高で同じ式（prepaidGate.ts inviteMostly）が成り立つ
  const lowIsInvite = !!p && (lowNotice?.detail?.invite === true || inviteMostly(p));
  // 残高の札の下の知らせ（赤の owed / 赤の out = 使い切った / 黄の low = 少ない / なし）は moneyDisplay.ts prepaidPageAlert で 1 つに決める。
  //   2026-11-05 money/zeroband: 一度も入れていない $0 の新しい人（prepaidBand 'start'・10/31 から招待なしの登録は全員 $0 で始まる）に
  //   黄色の「Your balance is running low … so nothing pauses」が出ていた（サーバーの balanceStatus 'out' → prepaidTone 'low'）。
  //   入れたことのない人に「少なくなった」は嘘 = 出さない（Core ３が 402 の文を同じ理由で直した: core/open-signup 6e0f5c123 servingGate.ts assertCanSpend）。
  //   その人には帯の代わりの文を足さない: すぐ下の入金の欄（「Add funds」と「Pay」のボタン）が次にすることを示す（写真 docs/teams/money/zeroband/screens/）
  //   以前は ranOut と黄色の条件をここに直に書いていた（テストできなかった）。式は同じで、'start' の low だけを止めた
  const band = p ? prepaidBand(p) : null;
  const alertKind = p ? prepaidPageAlert({ tone, band, lowNotice: !!lowNotice, dueNotice: !!dueNotice }) : null;
  // 2026-11-05 money/zerotone（上の続き）: 招待の確認待ち（band 'invitePending'・確認前は入金が 403）にも黄色の帯を出さない（prepaidPageAlert の WHY）。
  //   残高の札 2 枚の色も moneyDisplay.ts prepaidTileTones で決める = 一度も入れていない $0 の人（'start'）は警告の色にしない
  //   （以前はここに直に `tone === 'ok' || tone === 'idle' ? 'accent' : 'warn'` と `tone === 'low' || tone === 'owed'` を書いていた）
  const tiles = p ? prepaidTileTones({ tone, band }) : null;
  // 2026-11-05 money/pendingpay（上の続き）: 招待の確認待ちの札もふだんの色（prepaidTileTones）。
  //   メール未確認の人（確認待ち・招待なしで登録して確認前）は入金と自動チャージ ON を押せない形にする（moneyDisplay.ts prepaidFundingLocked の WHY）。
  //   以前は「Pay $26.66」が押せて、押すとサーバーの 403 email_unverified で赤い文が出るだけだった。上の確認の帯（再送のボタン）はそのまま
  const fundingLocked = prepaidFundingLocked({ band, emailVerified: developer?.emailVerified });
  const ranOutInvite = dueNotice?.detail?.invite === true || !!inviteLeft;
  const autoNotices = (p?.notices ?? []).filter((n) => n.kind === 'auto_topup_succeeded' || n.kind === 'auto_topup_failed').slice(0, 3);

  return (
    <>
      <PageHeader title={t('pp.title')} />
      {/* 説明は 1 行 + 下の段へのリンク（長い説明は PricingSection）。1 行は PriceNotes の 1 文目と同じ文（money.pricesFromProviders） */}
      <p className="page-lead muted">
        {t('money.pricesFromProviders')}
        {t('confirmPlain.sentenceGap')}
        <a href="#prepaid-pricing">{t('pp.howPricing')}</a>
      </p>
      {r.loading && !p && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('pp.title')} onRetry={r.reload} />}
      {notice && (
        <div className={`alert alert-${notice.tone}`} role="status">
          {notice.text}
        </div>
      )}

      {p && (
        <>
          <section className="tiles" aria-label={t('pp.title')}>
            <StatTile
              label={t('pp.balance')}
              value={money(p.balance, p.currency)}
              hint={
                lineHint?.kind === 'invite'
                  ? t('pp.balanceHintInvite', { line: money(lineHint.line, p.currency), setting: money(lineHint.setting, p.currency) })
                  : lineHint?.kind === 'setting'
                    ? t('pp.balanceHint', { line: money(lineHint.line, p.currency) })
                    : t('pp.balanceHintOff')
              }
              tone={tiles?.balance}
            />
            {p.owed > 0 && <StatTile label={t('pp.owed')} value={money(p.owed, p.currency)} hint={t('pp.owedHint')} tone="warn" />}
            <StatTile
              label={t('pp.runway')}
              value={p.owed > 0 ? t('pp.runwayZero') : typeof p.daysLeft === 'number' ? t('pp.runwayDays', { count: p.daysLeft }) : t('pp.runwayIdle')}
              hint={typeof p.daysLeft === 'number' && p.owed === 0 ? t('pp.runwayHint') : p.owed > 0 ? undefined : t('pp.runwayIdleHint')}
              tone={tiles?.runway}
            />
          </section>
          {/* 招待クレジットの内訳は残高の札のすぐ下（以前は説明の段落の並び = 残高より上にあった） */}
          {inviteLeft && (
            <p className="muted small prepaid-invite">{t('invite.card.credited', { amount: money(inviteLeft.remaining, p.currency), granted: money(inviteLeft.granted, p.currency) })}</p>
          )}

          {alertKind === 'owed' && (
            <div className="alert alert-error" role="alert">
              <strong>{t('pp.owedTitle')}</strong>
              <span>{t('pp.owedBody', { owed: money(p.owed, p.currency) })}</span>
            </div>
          )}
          {alertKind === 'out' && (
            <div className="alert alert-error" role="alert">
              <strong>{t(ranOutInvite ? 'pp.outTitleInvite' : 'pp.outTitle')}</strong>
              <span>{t('pp.outBody')}</span>
            </div>
          )}
          {alertKind === 'low' && (
            <div className="alert alert-warn" role="note">
              <strong>{t(lowIsInvite ? 'pp.lowTitleInvite' : 'pp.lowTitle')}</strong>
              <span>
                {lowIsInvite ? t('pp.lowBodyInvite') : typeof p.daysLeft === 'number' ? t('pp.lowBodyDays', { days: p.daysLeft }) : t('pp.lowBody')}
              </span>
            </div>
          )}
          {p.expiringSoon > 0 && <p className="muted small">{t('pp.expiringSoon', { amount: money(p.expiringSoon, p.currency) })}</p>}
          {autoNotices.map((n) => (
            <p key={`${n.kind}-${n.at}`} className={`alert ${n.kind === 'auto_topup_failed' ? 'alert-warn' : 'alert-ok'}`}>
              {n.kind === 'auto_topup_failed'
                ? t('pp.notice.auto_topup_failed', { ago: ago(n.at) })
                : t('pp.notice.auto_topup_succeeded', { ago: ago(n.at), amount: money(n.detail?.amount ?? 0, p.currency) })}
            </p>
          ))}

          <div className="money-grid">
            <TopUpCard
              currency={p.currency}
              limits={p.topUpLimits}
              locked={fundingLocked}
              onDone={(msg) => {
                setNotice({ tone: 'ok', text: msg });
                reloadAll();
              }}
            />
            <SettingsCard p={p} locked={fundingLocked} onSaved={() => r.reload()} />
          </div>
        </>
      )}

      <section className="card">
        <h2 className="card-title">{t('pp.history')}</h2>
        {ledger.error && <ErrorState error={ledger.error} what={t('pp.history')} onRetry={ledger.reload} />}
        {ledger.data && ledger.data.entries.length === 0 && <p className="muted small">{t('pp.noHistory')}</p>}
        {ledger.data && ledger.data.entries.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('pp.col.when')}</th>
                  <th>{t('pp.col.what')}</th>
                  <th className="num">{t('pp.col.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {ledger.data.entries.map((l, i) => (
                  <tr key={`${l.reference}-${i}`}>
                    <td title={fullDate(l.at)}>{ago(l.at)}</td>
                    <td>{sourceLabel(l.source)}</td>
                    <td className={`num ${l.amount < 0 ? '' : 'amount-plus'}`}>
                      {l.amount > 0 ? '+' : l.amount < 0 ? '−' : ''}
                      {money(Math.abs(l.amount), p?.currency ?? 'usd')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <LoadMore more={ledger.more} />
      </section>

      {p && <PricingSection currency={p.currency} limits={p.topUpLimits} />}
    </>
  );
}

/**
 * 料金のしくみ（上の 1 行のリンクの行き先）。以前は残高の上にあった説明の段落と、入金の欄にあった手数料の説明・例の表をここに集めた
 * （2026-10-18 design/rauno-dashboard）。文はどれも前のまま: pp.lead・PriceNotes（money.pricesFromProviders + money.refundOnly）・pp.feeNote・例の表
 */
function PricingSection({ currency, limits }: { currency: string; limits?: TopUpLimits }) {
  const { t } = useT();
  // 例の表（$0.50・$5・$20・$50 を入れると、いくら払うか）はサーバーの見積もりから（useTopUpExamples の WHY）
  const examples = useTopUpExamples(limits);
  return (
    <section className="card prepaid-pricing" id="prepaid-pricing" aria-labelledby="prepaid-pricing-title">
      <h2 className="card-title" id="prepaid-pricing-title">
        {t('pp.howPricing')}
      </h2>
      <p className="muted">{t('pp.lead')}</p>
      {/* 提供元の請求どおり（提供元の税を含む・2026-10-07 追記 7）・月末は戻すだけ（追記 2/3） */}
      <PriceNotes />
      <p className="muted small">{t('pp.feeNote')}</p>
      {/* 例の表（入れる額 → 払う額・手数料）。例はサーバーの見積もり（topup-quote）から = useTopUpExamples。
          2026-10-07 追記 9 A: 手数料は 6.2%・固定なし（0300）= 「少額ほど割合が大きい」とは言わない（pp.feeExamples は「例:」だけ） */}
      {examples.length > 0 && (
        <div className="topup-examples">
          <p className="muted small">{t('pp.feeExamples')}</p>
          <ul className="muted small">
            {examples.map((x) => (
              <li key={x.credited}>{t('pp.feeExample', { added: money(x.credited, currency), pay: money(x.charge, currency), fee: money(x.fee, currency) })}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function TopUpCard({
  currency,
  limits,
  locked,
  onDone
}: {
  currency: string;
  limits?: TopUpLimits;
  /** メール未確認 = サーバーが入金を 403 で断る人（moneyDisplay.ts prepaidFundingLocked）。「支払う」と「カードを保存」を押せない形にする */
  locked: boolean;
  onDone: (message: string) => void;
}) {
  const { t } = useT();
  const [preset, setPreset] = useState<number | 'other'>(2500);
  const [other, setOther] = useState('');
  const [save, setSave] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const idem = useRef(newIdempotencyKey());
  const amount = preset === 'other' ? parseAmountInput(other) : preset;
  const inRange = topUpInRange(amount, limits);
  // 2026-10-07 追記 8（founder「B がいいよ」）: 手数料は上乗せ = 選んだ額（amount）がきっちり残高に入り、カードにはそれ + 手数料を請求する。
  //   カードへの請求と手数料はサーバーの見積もり（GET /v1/money/prepaid/topup-quote・prepaid.ts grossUpTopUp）を読む = 画面に率・式を写さない
  //   （以前は topUpFee の率から moneyLogic の topUpFeeShare で割合を出していた）。範囲の外の額は入れられないので聞かない（範囲の注記の方を見てもらう）。
  //   見積もりが取れない（古い本体 = 道が無い・通信の失敗）ときは行を出さないだけ（入金そのものは止めない・結果の文が実際の額を言う）
  const quote = useResource<TopUpQuote>(inRange && amount !== null ? `/v1/money/prepaid/topup-quote?amount=${amount}` : null).data;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (locked || !inRange || amount === null) return;
    setBusy(true);
    setErr(null);
    // 戻り先はこの画面（Checkout の後に ?kumo_topup=<id> が付いて戻る）。キャンセルの戻り先には印を付ける（moneyDisplay.ts TOPUP_CANCELED_PARAM）
    const here = `${window.location.origin}/money/prepaid`;
    try {
      const res = await api<TopUpView>('/v1/money/prepaid/checkout', {
        method: 'POST',
        headers: { 'idempotency-key': idem.current },
        body: { amount, successUrl: here, cancelUrl: `${here}?${TOPUP_CANCELED_PARAM}=1`, saveForAutoTopUp: save }
      });
      idem.current = newIdempotencyKey();
      // 偽決済（鍵の無いローカル）はその場で決まる（completed か、テストで断る設定なら failed）。
      // WHY url より先に状態を見るか: 偽決済の応答は決まった後も url（戻り先）を持つ = url へ移ると「確認しています」を挟んでしまう
      const outcome = topUpOutcome(res);
      if (outcome === 'done') {
        onDone(
          // 応答の amount = カードに請求した額・credited = 残高に入った額・fee = その差（0299 から。上乗せ）
          `${t('pp.topUpDone', { amount: money(res.credited ?? res.amount, currency) })}${res.fee ? ` ${t('pp.chargedFee', { charge: money(res.amount, currency), fee: money(res.fee, currency) })}` : ''}`
        );
        setBusy(false);
        return;
      }
      if (outcome !== 'checking') {
        setErr(topUpNotice(outcome, res, t).text);
        setBusy(false);
        return;
      }
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
      setErr(t('pp.topUpFailed'));
      setBusy(false);
    } catch (e2) {
      setErr(errorText(e2 as ApiError));
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">{t('pp.topUp')}</h2>
      <div className="seg preset-seg" role="group" aria-label={t('pp.amount')}>
        {PRESETS.map((c) => (
          <button key={c} type="button" className={`seg-btn ${preset === c ? 'is-active' : ''}`} aria-pressed={preset === c} onClick={() => setPreset(c)}>
            {money(c, currency)}
          </button>
        ))}
        <button
          type="button"
          className={`seg-btn ${preset === 'other' ? 'is-active' : ''}`}
          aria-pressed={preset === 'other'}
          onClick={() => setPreset('other')}
        >
          {t('pp.other')}
        </button>
      </div>
      {preset === 'other' && (
        <label className="field">
          <span>{t('pp.otherAmount')}</span>
          <input inputMode="decimal" value={other} onChange={(e) => setOther(e.target.value)} placeholder={limits ? amountToInput(limits.min) : undefined} autoFocus />
          {/* 範囲はサーバーが返したときだけ出す（古いサーバーでは古い数字を見せない） */}
          {limits && (
            <small className={other && !inRange ? 'field-error' : 'muted'}>{t('pp.range', { min: money(limits.min, currency), max: money(limits.max, currency) })}</small>
          )}
        </label>
      )}
      <label className="check">
        {/* カードの保存も入金と同じ POST（checkout）= 確認前は同じ 403。押せない形にそろえる */}
        <input type="checkbox" checked={save && !locked} disabled={locked} onChange={(e) => setSave(e.target.checked)} />
        <span>{t('pp.saveCard')}</span>
      </label>
      {quote && (
        <p className="topup-breakdown" aria-live="polite">
          {t('pp.feeLine', { added: money(quote.credited, currency), pay: money(quote.charge, currency), fee: money(quote.fee, currency) })}
        </p>
      )}
      {/* 手数料の長い説明（feeNote）と例の表は下の「料金のしくみ」（PricingSection）。ここは見積もりの 1 行だけ */}
      {err && (
        <p className="alert alert-error" role="alert">
          {err}
        </p>
      )}
      {/* 主のボタン = この画面で 1 つ。入金の欄の幅いっぱい（以前は右下の小さなボタンで、スマホでは見落としやすかった） */}
      {/* 押せない理由と、押せるようになる方法をボタンのすぐ上に 1 行（確かめ方そのものは上の帯 = verifyBanner と再送のボタン）。
          金額の候補と見積もりの 1 行は残す = 確かめた後にいくら払うかは今も分かる */}
      {locked && (
        <p className="muted small" id="topup-locked">
          {t('pp.lockedUnverified')}
        </p>
      )}
      <div className="form-actions">
        <button className="btn btn-primary btn-block" disabled={locked || busy || !inRange} aria-describedby={locked ? 'topup-locked' : undefined}>
          {/* ボタンは実際に払う額（上乗せ後・サーバーの見積もり）。見積もりが取れない間は選んだ額のまま */}
          {busy ? t('pp.paying') : t('pp.pay', { amount: quote ? money(quote.charge, currency) : amount !== null ? money(amount, currency) : '—' })}
        </button>
      </div>
    </form>
  );
}

/**
 * 例の表の額（残高に入れる額）。払う額と手数料を数字で見せる（2026-10-07 追記 9 A から手数料は 6.2%・固定なし = どの額でも割合はほぼ同じ）。
 * WHY $10 でなく $20 か: サイトの pricing と同じ例にそろえる。サイトでは「$10」が隠した販売の機能の値段の例として検査に止められる（apps/site features.test.mjs）
 */
const EXAMPLE_CREDITS = [50, 500, 2000, 5000] as const;

/**
 * 例の表の行（入れる額 → 払う額・手数料）。2026-10-07 console/usage-view・founder「そのことはクリエイターに伝えておきたい」。
 * WHY 4 本 GET するか: 式を画面に写さない（Money の決定）。見積もりは軽い読み取りで、画面を開いたときに 1 回だけ。
 * 額はサーバーの見積もり（GET /v1/money/prepaid/topup-quote）= 画面に率・式を写さない（Money の決定）。
 * その人の下限〜上限の外の額は聞かない（選べない額の例で迷わせない）。取れない行は出さない（古い本体では表ごと出ない）。
 * フックの数を固定するため、額ごとに 1 本ずつ useResource を呼ぶ（範囲外は path = null で読まない）
 */
function useTopUpExamples(limits: TopUpLimits | undefined): TopUpQuote[] {
  const path = (c: number) => (topUpInRange(c, limits) ? `/v1/money/prepaid/topup-quote?amount=${c}` : null);
  const a = useResource<TopUpQuote>(path(EXAMPLE_CREDITS[0])).data;
  const b = useResource<TopUpQuote>(path(EXAMPLE_CREDITS[1])).data;
  const c = useResource<TopUpQuote>(path(EXAMPLE_CREDITS[2])).data;
  const d = useResource<TopUpQuote>(path(EXAMPLE_CREDITS[3])).data;
  return [a, b, c, d].filter((x): x is TopUpQuote => !!x);
}

function SettingsCard({ p, locked, onSaved }: { p: PrepaidView; locked: boolean; onSaved: () => void }) {
  const { t } = useT();
  const [line, setLine] = useState(amountToInput(p.lowBalanceCents));
  const [autoOn, setAutoOn] = useState(p.autoTopUp.enabled);
  const [below, setBelow] = useState(amountToInput(p.autoTopUp.belowCents));
  const [add, setAdd] = useState(amountToInput(p.autoTopUp.amountCents));
  const [fund, setFund] = useState(p.autoFundFromEarnings);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // カードの表示名（card）は本物の Stripe で保存したときだけ入る。偽決済や古い保存では無いこともあるので、チェック自体は止めない
  // （保存したカードが無ければサーバーが 409 no_saved_payment_method を返し、その文言で次の一手を出す）
  const hasCard = !!p.autoTopUp.card;
  const addCents = parseAmountInput(add);
  // 自動チャージ 1 回あたりの目安も入金と同じサーバーの見積もり（topup-quote）。範囲外・古い本体（道が無い）では出さない
  const autoFee = useResource<TopUpQuote>(autoOn && topUpInRange(addCents, p.topUpLimits) && addCents !== null ? `/v1/money/prepaid/topup-quote?amount=${addCents}` : null).data;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const lowBalanceCents = parseAmountInput(line);
    const belowCents = parseAmountInput(below);
    const amountCents = parseAmountInput(add);
    if (lowBalanceCents === null || belowCents === null || amountCents === null) {
      setMsg({ ok: false, text: t('error.invalid_amount') });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api('/v1/money/prepaid/settings', {
        method: 'PUT',
        body: { lowBalanceCents, autoFundFromEarnings: fund, autoTopUp: { enabled: autoOn, belowCents, amountCents } }
      });
      setMsg({ ok: true, text: t('pp.saved') });
      onSaved();
    } catch (e2) {
      setMsg({ ok: false, text: errorText(e2 as ApiError) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">{t('pp.settings')}</h2>
      <div className="stack">
        <label className="field">
          <span>{t('pp.lowLine')}</span>
          <input inputMode="decimal" value={line} onChange={(e) => setLine(e.target.value)} />
          <small className="muted">{t('pp.lowLineHint')}</small>
        </label>

        <fieldset className="subfield">
          <legend>{t('pp.auto')}</legend>
          {/* 止まらない唯一の方法 = 自動チャージ（2026-10-04 Kumo 管理: 残高 0 の猶予は作らない = 立て替えが生まれる）。ここで理由ごと案内する */}
          <small className="muted">{t('pp.autoWhy')}</small>
          <label className="check">
            {/* メール未確認（moneyDisplay.ts prepaidFundingLocked）: ON にして保存するとサーバーが 403 = オフのときは押せない形。
                もし ON のままの人がいても（確認前に付くことは無いはず）オフに戻す操作は止めない（サーバーも断らない） */}
            <input type="checkbox" checked={autoOn} disabled={locked && !autoOn} onChange={(e) => setAutoOn(e.target.checked)} />
            <span>{autoOn ? t('pp.autoOn') : t('pp.autoOff')}</span>
          </label>
          {/* 確認前は「一度入金すると…」（pp.autoNoCard）も今はできない一手 = 代わりに確認の 1 行 */}
          {locked && !autoOn && <small className="muted">{t('pp.autoLockedUnverified')}</small>}
          {!locked && !hasCard && !autoOn && <small className="muted">{t('pp.autoNoCard')}</small>}
          {p.autoTopUp.card && <small className="muted">{t('pp.autoCard', { card: p.autoTopUp.card })}</small>}
          {p.autoTopUp.lastFailedAt && <small className="field-error">{t('pp.autoFailed', { ago: ago(p.autoTopUp.lastFailedAt) })}</small>}
          {autoOn && (
            <div className="form-grid form-grid-top">
              <label className="field">
                <span>{t('pp.autoBelow')}</span>
                <input inputMode="decimal" value={below} onChange={(e) => setBelow(e.target.value)} />
              </label>
              <label className="field">
                <span>{t('pp.autoAmount')}</span>
                <input inputMode="decimal" value={add} onChange={(e) => setAdd(e.target.value)} />
                {/* 1 回あたりの手数料の目安（入金の欄と同じ式・2026-10-07 console/usage-view）。範囲外・率の無い古い本体では出さない */}
                {autoFee && (
                  <small className="muted">
                    {t('pp.autoFee', { pay: money(autoFee.charge, p.currency), added: money(autoFee.credited, p.currency), fee: money(autoFee.fee, p.currency) })}
                  </small>
                )}
              </label>
            </div>
          )}
        </fieldset>

        {/* 「売上から先に充てる」は売上（payouts）を見せない間は出さない（2026-09-29 creator/launch-money・launchHidden.ts）。
            送る値は今の設定のまま（fund は読んだ値 = 変えない）。売上が無いのでサーバーでも実質何もしない（Money の確認 2026-09-29） */}
        {!isLaunchHidden('payouts') && (
          <label className="check">
            <input type="checkbox" checked={fund} onChange={(e) => setFund(e.target.checked)} />
            <span>
              {t('pp.autoFund')}
              <small className="muted block">{t('pp.autoFundHint')}</small>
            </span>
          </label>
        )}
      </div>
      {msg && (
        <p className={`alert ${msg.ok ? 'alert-ok' : 'alert-error'}`} role="status">
          {msg.text}
        </p>
      )}
      {/* 次のボタン（主は入金の「支払う」1 つ・2026-10-18） */}
      <div className="form-actions">
        <button className="btn" disabled={busy}>
          {busy ? t('common.working') : t('pp.save')}
        </button>
      </div>
    </form>
  );
}
